/**
 * 报警面板组件
 *
 * 显示最近报警列表，支持标记已处理
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Drawer,
  List,
  Tag,
  Button,
  Empty,
  Space,
  Typography,
  Tooltip,
  Badge,
  Spin,
  message,
} from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  BellOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  UnorderedListOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { AlarmRecord, useUIStore, useUserStore } from '@/store/useDeviceStore'
import { get, put } from '@/utils/request'

const { Text } = Typography

interface AlarmPanelProps {
  visible: boolean
  onClose: () => void
}

function AlarmPanel({ visible, onClose }: AlarmPanelProps) {
  const navigate = useNavigate()
  const { alarms, addAlarms, markAlarmHandled } = useUIStore()
  const { token } = useUserStore()
  const [loading, setLoading] = useState(false)
  const [handlingId, setHandlingId] = useState<number | null>(null)
  const [batchHandling, setBatchHandling] = useState(false)

  // 加载服务器报警
  const loadServerAlarms = useCallback(async () => {
    if (!token) return

    setLoading(true)
    try {
      const data = await get<{ items: AlarmRecord[] }>('/api/v1/alarms/unhandled', {
        showError: false,
      })
      addAlarms(
        data.items.map((alarm) => ({
          ...alarm,
          handled: alarm.handled ?? false,
        }))
      )
    } catch (error) {
      console.error('加载报警失败:', error)
    } finally {
      setLoading(false)
    }
  }, [addAlarms, token])

  // 处理报警
  const handleAlarm = async (alarmId: number) => {
    if (!token) return

    setHandlingId(alarmId)
    try {
      await put(`/api/v1/alarms/${alarmId}/handle`)
      message.success('报警已处理')
      markAlarmHandled(alarmId)
    } catch (error) {
      // 错误已在request中处理
    } finally {
      setHandlingId(null)
    }
  }

  const handleAllAlarms = async () => {
    if (!token) return

    const ids = unhandledAlarms.map((alarm) => alarm.id)
    if (ids.length === 0) {
      message.info('当前没有未读报警')
      return
    }

    setBatchHandling(true)
    try {
      await put('/api/v1/alarms/batch-handle', ids)
      ids.forEach((id) => markAlarmHandled(id))
      message.success(`已将 ${ids.length} 条报警标记为已读`)
    } catch (error) {
      // 错误已在request中处理
    } finally {
      setBatchHandling(false)
    }
  }

  // 面板打开时加载数据
  useEffect(() => {
    if (visible) {
      loadServerAlarms()
    }
  }, [loadServerAlarms, visible])

  const unhandledAlarms = useMemo(
    () =>
      alarms
        .filter((alarm) => !alarm.handled)
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ),
    [alarms]
  )

  // 统计
  const dangerCount = unhandledAlarms.filter((a) => a.level === 'danger').length
  const warningCount = unhandledAlarms.filter((a) => a.level === 'warning').length

  return (
    <Drawer
      title={
        <div className="alarm-panel-title">
          <div className="alarm-panel-title-icon">
            <BellOutlined />
          </div>
          <div className="alarm-panel-title-content">
            <Text strong className="alarm-panel-title-text">
              报警中心
            </Text>
            <Text type="secondary" className="alarm-panel-title-subtext">
              实时查看未处理报警
            </Text>
          </div>
          <Badge
            count={unhandledAlarms.length}
            overflowCount={99}
            className="alarm-panel-title-badge"
          />
        </div>
      }
      placement="right"
      width={432}
      open={visible}
      onClose={onClose}
      className="alarm-panel-drawer"
    >
      <div className="alarm-panel-toolbar">
        <div className="alarm-panel-summary">
          <div className="alarm-panel-summary-chip danger">
            <ExclamationCircleOutlined />
            <span>危险</span>
            <strong>{dangerCount}</strong>
          </div>
          <div className="alarm-panel-summary-chip warning">
            <WarningOutlined />
            <span>警告</span>
            <strong>{warningCount}</strong>
          </div>
        </div>

        <Space wrap size={[8, 8]} className="alarm-panel-actions">
          <Button
            icon={<CheckOutlined />}
            onClick={handleAllAlarms}
            loading={batchHandling}
            disabled={unhandledAlarms.length === 0}
          >
            一键已读
          </Button>
          <Button icon={<SyncOutlined />} onClick={loadServerAlarms}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<UnorderedListOutlined />}
            onClick={() => {
              onClose()
              navigate('/alarms')
            }}
          >
            查看全部
          </Button>
        </Space>
      </div>

      {/* 报警列表 */}
      <Spin spinning={loading}>
        {unhandledAlarms.length === 0 ? (
          <Empty description="暂无报警" />
        ) : (
          <List
            className="alarm-panel-list"
            dataSource={unhandledAlarms}
            renderItem={(alarm) => (
              <List.Item
                className={`alarm-panel-list-item alarm-panel-list-item-${alarm.level}`}
                actions={[
                  <Tooltip title="标记已处理" key="handle">
                    <Button
                      className="alarm-panel-handle-btn"
                      size="small"
                      icon={<CheckOutlined />}
                      loading={handlingId === alarm.id}
                      onClick={() => handleAlarm(alarm.id)}
                    >
                      处理
                    </Button>
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    alarm.level === 'danger' ? (
                      <ExclamationCircleOutlined
                        style={{ fontSize: 24, color: '#ff4d4f' }}
                      />
                    ) : (
                      <WarningOutlined
                        style={{ fontSize: 24, color: '#faad14' }}
                      />
                    )
                  }
                  title={
                    <Space size={8} wrap>
                      <Tag color={alarm.level === 'danger' ? 'red' : 'orange'}>
                        {alarm.level === 'danger' ? '危险' : '警告'}
                      </Tag>
                      <Text strong>{alarm.dev_id}</Text>
                    </Space>
                  }
                  description={
                    <div className="alarm-panel-item-description">
                      <Text className="alarm-panel-item-message">{alarm.msg}</Text>
                      <Text type="secondary" className="alarm-panel-item-time">
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {new Date(alarm.created_at).toLocaleString()}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </Drawer>
  )
}

export default AlarmPanel
